import React, { useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Button, ButtonSize } from "@yoti/ui-kit-components-react/button";
import {
  SwitchButton,
  SwitchButtonSize,
} from "@yoti/ui-kit-components-react/switch-button";
import styles from "./DataCollection.module.scss";
import { MetadataClient } from "../../api/metadata";
import { Config } from "../../utils/config";
import { ROUTER_PATHS } from "../../constants/constants";
import { Footer } from "../Footer/Footer";
import { Alert } from "../Alert/Alert";
import { Title } from "../Title/Title";
import { getAllData } from "../../services/data/getAllData";
import { Panel } from "../Panel/Panel";
import { DisplayData } from "../DisplayData/DisplayData";
import { Checkbox } from "../Checkbox/Checkbox";
import { Loading } from "@yoti/ui-kit-components-react/loading";
import SelectCameraDevices from "./SelectDevices/SelectCameraDevices";
import SelectAccelerometerDevices from "./SelectDevices/SelectAccelerometerDevices";
import SelectGyroscopeDevices from "./SelectDevices/SelectGyroscopeDevices";
import SelectGPSDevices from "./SelectDevices/SelectGPSDevices";
import SelectUserVerifyDevices from "./SelectDevices/SelectUserVerifyDevices";
import SelectMicrophoneDevices from "./SelectDevices/SelectMicrophoneDevices";
import SelectNavigatorProperties from "./SelectDevices/SelectNavigatorProperties";
import Description from "./SelectDevices/Description";
import FakeType from "./SelectDevices/FakeType";

export const ERROR_MESSAGE = `Your data could not be collected. Please, ensure that all your
                          devices are plugged in and you have Internet connection and try again.`;

const metadataClient = new MetadataClient(Config.apiUrl);

export const DataCollection = () => {
  const location = useLocation(); // The location object.
  const videoRef = useRef(null); // The reference to the video element.
  const [data, setData] = useState(); // The captured data.
  const [displayAlert, setDisplayAlert] = useState(false); // If the error alert is displayed.
  const [isRealData, setIsRealData] = useState(true); // If the data captured is real.
  const [isSelectingDevices, setSelectingDevices] = useState(false); // If the user is selecting the devices.
  const [isLoading, setIsLoading] = useState(false); // If the data is being processed.
  const [currentProgress, setCurrentProgress] = useState(null); // Current progress step: { current, total, name }.
  const [agrees, setAgrees] = useState(false); // If the user gives its consent to share its data.
  const [displayingData, setDisplayingData] = useState(false); // If the captured data is being displayed.
  const [fakeType, setFakeType] = useState('');
  const [description, setDescription] = useState('');
  const [devicesLoading, setDevicesLoading] = useState(0);
  const [selectedDevices, setSelectedDevices] = useState({
    camera: [],
    accelerometer: [],
    gyroscope: [],
    gps: [],
    userVerify: [],
    microphone: [],
    navigator: [],
  }); // The devices selected to be analyzed and shared.

  const oauthData = location.state?.oauthData;

  const onClick = () => {
    setSelectingDevices(true);
  };

  const onShare = async () => {
    setIsLoading(true);
    setCurrentProgress(null);
    try {
      const data = await getAllData(
        videoRef.current,
        selectedDevices,
        (currentStep, totalSteps, stepName) => {
          setCurrentProgress({
            current: currentStep,
            total: totalSteps,
            name: stepName,
          });
        },
      );
      const dataToShare = {
        ...data,
        isFake: !isRealData,
        description,
        ...(!isRealData && { fakeType }),
      };
      const insertedId = await metadataClient.insert(dataToShare, oauthData);
      setData({ databaseID: insertedId, ...dataToShare });
      setDisplayAlert(false);
      setSelectingDevices(false);
    } catch (err) {
      console.error(err);
      setDisplayAlert(true);
      setData(undefined);
    } finally {
      setIsLoading(false);
      setCurrentProgress(null);
    }
  };

  const onSelectedChange = (newValue, key) => {
    setSelectedDevices((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const loadingDevices = {
    add: (count) => {
      setDevicesLoading((prev) => prev + count);
    },
    done: () => {
      setDevicesLoading((prev) => prev - 1);
    },
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  if (!oauthData) {
    return <Navigate to={`/${ROUTER_PATHS.LOGIN}`} replace />;
  }

  return (
    <div className={styles.root}>
      <Title dataWasRetrieved={!!data && isSelectingDevices} />
      <br />
      {!isSelectingDevices ? (
        <div>
          <Panel>{!data ? <ConsentText /> : <ThankText />}</Panel>
          {!data && !isLoading && (
            <>
              <SelectDataType
                isRealData={isRealData}
                setIsRealData={setIsRealData}
              />
              <SelectAgree agrees={agrees} setAgrees={setAgrees} />
            </>
          )}
          <div className={styles.root__button}>
            <ShareButton
              hasShared={!!data}
              disabled={!agrees || isLoading}
              onClick={onClick}
              displayingData={displayingData}
              setDisplayingData={setDisplayingData}
            />
          </div>
          <div>{displayingData && <DisplayData data={data} />}</div>
        </div>
      ) : (
        <div>
          <h2>
            Here is a list of your devices and sensors. Please, select only the
            ones that represent {isRealData ? "real" : "fake"} data.
          </h2>
          <SelectCameraDevices
            onChange={(val) => onSelectedChange(val, 'camera')}
            selectedDevices={selectedDevices.camera}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectAccelerometerDevices
            onChange={(val) => onSelectedChange(val, 'accelerometer')}
            selectedDevices={selectedDevices.accelerometer}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectGyroscopeDevices
            onChange={(val) => onSelectedChange(val, 'gyroscope')}
            selectedDevices={selectedDevices.gyroscope}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectGPSDevices
            onChange={(val) => onSelectedChange(val, 'gps')}
            selectedDevices={selectedDevices.gps}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectUserVerifyDevices
            onChange={(val) => onSelectedChange(val, 'userVerify')}
            selectedDevices={selectedDevices.userVerify}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectMicrophoneDevices
            onChange={(val) => onSelectedChange(val, 'microphone')}
            selectedDevices={selectedDevices.microphone}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          <SelectNavigatorProperties
            onChange={(val) => onSelectedChange(val, 'navigator')}
            selectedDevices={selectedDevices.navigator}
            loadingDevices={loadingDevices}
            disabled={isLoading}
          />
          {!isRealData && (
            <FakeType value={fakeType} onChange={setFakeType} />
          )}
          <Description value={description} onChange={handleDescriptionChange} />
          <div className={styles.root__button}>
            <Button
              onClick={onShare}
              size="Small"
              fullWidth
              disabled={
                isLoading ||
                devicesLoading > 0 ||
                (!isRealData && fakeType === "")
              }
            >
              Share data
            </Button>
          </div>
          {currentProgress && (
            <h4>
              Step {currentProgress.current}/{currentProgress.total} - {currentProgress.name}
            </h4>
          )}
        </div>
      )}
      {isLoading && <Loading />}
      {displayAlert && (
        <Alert title="Error while sharing your data" message={ERROR_MESSAGE} />
      )}
      <video autoPlay ref={videoRef} />
      <Footer />
    </div>
  );
};

const ConsentText = () => (
  <div className={styles.root__terms}>
    <p>
      We are collecting your device metadata to improve our SICAP protections
      against virtual camera device attacks when using the Web face capture
      module.
    </p>
    <p>
      We need device metadata from a variety of devices. We will only use this
      data for this purpose and then it will be deleted once the testing is
      complete. We will only ever keep the data for 6 months. Metadata will be
      stored in a temporary database in our AI Services gCloud project.
    </p>
    <p>
      We may reach out to contact you by email to follow up on this testing, if
      you do not wish to be contacted please contact Miguel. If you want to
      withdraw your consent then please email Miguel at miguel.jimenez@yoti.com.
    </p>
    <p>
      <strong>NOTE: </strong> If you are going to perform Real data collection
      and are currently using a virtual camera service, please do not proceed
      until you disable it.
    </p>
    <p>
      Please, make sure <strong>your cameras are not being used</strong> by any
      other application or browser tab before starting the data collection.
    </p>
    <p>
      If you select sensor data collection, remember to enable the
      <strong> permissions</strong> to get them.
    </p>
    <p>
      In the metadata collection process,{" "}
      <strong>the tool will open and display the camera preview</strong>, we
      need to do it in order to calculate the camera frame rate for all
      available resolutions.{" "}
      <strong>Important: No images are being saved.</strong>
    </p>
  </div>
);

const ThankText = () => (
  <p>Thank you so much for sharing your devices metadata.</p>
);

const SelectDataType = ({ isRealData, setIsRealData }) => (
  <>
    <p className={styles.data_type_text}>What type of data will you share?</p>
    <div className={styles.switch_container}>
      <div className={styles.switch}>
        <label>Fake</label>
        <SwitchButton
          id="storybook-switch"
          checked={isRealData}
          size={SwitchButtonSize.Large}
          onChange={() => setIsRealData((prev) => !prev)}
        />
        <label>Real</label>
      </div>
    </div>
  </>
);

const SelectAgree = ({ agrees, setAgrees }) => (
  <Checkbox
    label="I accept to share my data with Yoti"
    checked={agrees}
    onChange={() => setAgrees((prev) => !prev)}
  />
);

const ShareButton = ({
  hasShared,
  disabled,
  onClick,
  displayingData,
  setDisplayingData,
}) =>
  !hasShared ? (
    <Button
      onClick={onClick}
      size={ButtonSize.Small}
      fullWidth
      disabled={disabled}
    >
      Share data
    </Button>
  ) : (
    <Button
      onClick={() => setDisplayingData((prev) => !prev)}
      size={ButtonSize.Small}
      fullWidth
    >
      {!displayingData ? "Show" : "Hide"} shared data
    </Button>
  );
